// Pure helpers for the city building list: display names for building ids and
// the "one at a time" guard for single-instance buildings (the sim lets the
// same building be queued repeatedly while the first is still in progress).

import type { MessageKey, Translate } from '../../i18n/index.js';

/**
 * Buildings that must never be queued twice (each queue entry pays the full cost): the
 * Champion summoning sites (`legendRows.kinds[*].siteBuilding`).
 */
const SINGLE_INSTANCE: ReadonlySet<string> = new Set(['dragon_roost', 'golem_forge', 'necropolis']);

export function isSingleInstanceBuilding(id: string): boolean {
  return SINGLE_INSTANCE.has(id);
}

/** Ids with a localized name; everything else uses the catalog name from the sim. */
const NAME_KEYS: Readonly<Record<string, MessageKey>> = {
  dragon_roost: 'champion.site.dragon_roost' as MessageKey,
  golem_forge: 'champion.site.golem_forge' as MessageKey,
  necropolis: 'champion.site.necropolis' as MessageKey,
};

interface NamedDef {
  readonly id: string;
  readonly name: string;
}

/** "dragon_roost" -> "Dragon Roost" (last-resort label for ids with no catalog entry). */
export function humanizeBuildingId(id: string): string {
  return id
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Display name of a building id: i18n key, else the catalog name, else a humanized id. */
export function buildingLabel(t: Translate, id: string, defs: ReadonlyArray<NamedDef>): string {
  const key = NAME_KEYS[id];
  if (key) return t(key);
  return defs.find((d) => d.id === id)?.name ?? humanizeBuildingId(id);
}

export interface QueuedBuilding {
  readonly buildingId: string;
  readonly isUnit: boolean;
}

/**
 * True when `id` is a single-instance building that is already being built or
 * is waiting in the queue. Other buildings keep the generic behaviour.
 */
export function isBuildAlreadyQueued(
  id: string,
  buildingProgress: string | undefined,
  queue: ReadonlyArray<QueuedBuilding> | undefined,
): boolean {
  if (!isSingleInstanceBuilding(id)) return false;
  if (buildingProgress === id) return true;
  return (queue ?? []).some((q) => !q.isUnit && q.buildingId === id);
}
